import React, { useCallback, useEffect, useState } from 'react';
import { ChecklistState } from '../../model/ChecklistState';
import LocalStorage from '../../common/LocalStorage';
import ChecklistInfo from '../../model/ChecklistInfo';
import Checklist from './Checklist';

interface Props {
  checklistInfo: ChecklistInfo[];
  contentId: string;
  header?: string;
  listName: string;
  onAllChecked?: (checked: boolean) => void;
  workflowId: number;
  shouldResetChecklist?: boolean;
  handleResetChecklist?: () => void;
}

const ChecklistGroup: React.FunctionComponent<Props> = (props: Props) => {
  const {
    checklistInfo,
    contentId,
    header,
    listName,
    onAllChecked,
    workflowId,
    shouldResetChecklist,
    handleResetChecklist,
  } = props;

  const itemsCheckedStates = Array(checklistInfo.length);
  checklistInfo.forEach((checklist, index) => {
    itemsCheckedStates[index] = Array(checklist.items.length).fill(false);
  });
  const [itemsChecked, setItemsChecked] = useState<boolean[][]>(itemsCheckedStates);

  useEffect(() => {
    loadChecklistState();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!!shouldResetChecklist) {
      clearChecked();
      if (handleResetChecklist) {
        handleResetChecklist();
      }
    }
  }, [shouldResetChecklist]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateAllChecked = useCallback(() => {
    const allItemsChecked = itemsChecked.every((listItems) => listItems.every((item) => item));
    if (onAllChecked) {
      onAllChecked(allItemsChecked);
    }
  }, [itemsChecked, onAllChecked]);

  useEffect(() => {
    updateAllChecked();
  }, [itemsChecked, updateAllChecked]);

  const clearChecked = () => {
    resetChecklistState();
    checklistInfo.forEach((checklist, index) => {
      itemsCheckedStates[index] = Array(checklist.items.length).fill(false);
    });
    setItemsChecked(itemsCheckedStates);
  };

  const checklistStateMatches = (checklistState: ChecklistState): boolean => {
    const { workflowId: checklistWorkflowId } = checklistState;
    return checklistWorkflowId >= workflowId;
  };

  const loadChecklistState = () => {
    const checklistState: ChecklistState = LocalStorage.getChecklistState(contentId, listName);
    if (checklistState) {
      if (!checklistStateMatches(checklistState) || !!shouldResetChecklist) {
        resetChecklistState();
      } else {
        const { itemStates } = checklistState;
        if (itemStates) {
          setItemsChecked(itemStates);
        }
      }
    }
  };

  const resetChecklistState = () => {
    LocalStorage.resetChecklistState(contentId, listName);
  };

  const saveChecklistState = (updatedItemStates: boolean[][]) => {
    const checklistState = ChecklistState.createNew(updatedItemStates, workflowId);
    LocalStorage.setChecklistState(contentId, checklistState, listName);
  };

  const handleItemChecked = (listIndex: number, itemIndex: number, checked: boolean) => {
    const newItemsChecked = itemsChecked.slice();
    // @ts-expect-error TS2532 - strict migration
    newItemsChecked[listIndex][itemIndex] = checked;
    setItemsChecked(newItemsChecked);
    saveChecklistState(newItemsChecked);
  };

  const allChecklists = checklistInfo.map((listInfo, index) => {
    return (
      <Checklist
        // @ts-expect-error TS2322 - strict migration
        checkedStatus={itemsChecked[index]}
        key={listInfo.instruction}
        checklistIndex={index}
        checklistInfo={listInfo}
        onChecked={handleItemChecked}
      />
    );
  });

  return (
    <div>
      {header}
      {allChecklists}
    </div>
  );
};

export default ChecklistGroup;
